import React, { useState, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { 
  FaPlus, 
  FaEye, 
  FaEdit, 
  FaTrash, 
  FaSearch, 
  FaFilter, 
  FaClipboardList,
  FaCar,
  FaUser,
  FaCalendarAlt,
  FaCheckCircle,
  FaClock,
  FaExclamationTriangle,
  FaChevronDown,
  FaChevronUp,
  FaGasPump,
  FaTachometerAlt,
  FaImage,
  FaVideo,
  FaMicrophone,
  FaCarSide,
  FaHandPaper,
  FaClipboardCheck,
  FaCalendarDay
} from 'react-icons/fa';
import { handoverService, rentals, employeeService } from '../../../services/api';
import HandoverForm from '../../../pages/HandoverForm';
import HandoverDetailsModal from '../../../components/modals/HandoverDetailsModal';
import LoadingSpinner from '../../../components/LoadingSpinner/LoadingSpinner';
import moment from 'moment';

const Handovers = () => {
  const [handovers, setHandovers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [showDetailsModal, setShowDetailsModal] = useState(false);
  const [selectedHandover, setSelectedHandover] = useState(null);
  const [expandedHandover, setExpandedHandover] = useState(null);
  const [deleting, setDeleting] = useState(null);
  const [activeTab, setActiveTab] = useState('pickup');
  
  // Filters and pagination
  const [filters, setFilters] = useState({
    employee: '',
    search: ''
  });
  const [currentPage, setCurrentPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [showFilters, setShowFilters] = useState(false);
  
  // Available options for filters
  const [employees, setEmployees] = useState([]);
  const [availableRentals, setAvailableRentals] = useState([]);

  const tabs = [
    {
      id: 'pickup',
      name: 'Pickup Handovers',
      icon: FaCarSide,
      color: 'text-green-600',
      bgColor: 'bg-green-50',
      borderColor: 'border-green-200'
    },
    {
      id: 'return',
      name: 'Return Handovers',
      icon: FaClipboardCheck,
      color: 'text-blue-600',
      bgColor: 'bg-blue-50',
      borderColor: 'border-blue-200'
    },
    {
      id: 'today',
      name: "Today's Handovers",
      icon: FaCalendarDay,
      color: 'text-purple-600',
      bgColor: 'bg-purple-50',
      borderColor: 'border-purple-200'
    }
  ];

  useEffect(() => {
    fetchHandovers();
    fetchEmployees();
    fetchAvailableRentals();
  }, [currentPage, filters, activeTab]);

  const fetchHandovers = async () => {
    try {
      setLoading(true);
      const queryParams = {
        page: currentPage,
        limit: 5,
        ...filters
      };

      // Add tab-specific filters
      if (activeTab === 'pickup') {
        queryParams.handoverType = 'pickup';
      } else if (activeTab === 'return') {
        queryParams.handoverType = 'return';
      } else if (activeTab === 'today') {
        // Use createdAt instead of handoverDate to show handovers done today
        const today = moment().format('YYYY-MM-DD');
        queryParams.createdStartDate = today;
        queryParams.createdEndDate = today;
      }
      
      // Remove empty filters
      Object.keys(queryParams).forEach(key => {
        if (!queryParams[key]) delete queryParams[key];
      });

      const response = await handoverService.getHandovers(queryParams);
      setHandovers(response.data.handovers || []);
      setTotalPages(response.data.pagination?.pages || 1);
    } catch (err) {
      setError('Failed to load handovers');
      console.error('Error fetching handovers:', err);
    } finally {
      setLoading(false);
    }
  };

  const fetchEmployees = async () => {
    try {
      const response = await employeeService.getAllEmployees();
      setEmployees(response.data || []);
    } catch (err) {
      console.error('Error fetching employees:', err);
    }
  };

  const fetchAvailableRentals = async () => {
    try {
      const response = await rentals.getAll();
      setAvailableRentals(response.data || []);
    } catch (err) {
      console.error('Error fetching rentals:', err);
    }
  };

  const handleCreateHandover = () => {
    setShowCreateModal(true);
  };

  const handleViewHandover = (handover) => {
    setSelectedHandover(handover);
    setShowDetailsModal(true);
  };

  const handleDeleteHandover = async (handoverId) => {
    if (!window.confirm('Are you sure you want to delete this handover?')) {
      return;
    }

    try {
      setDeleting(handoverId);
      await handoverService.deleteHandover(handoverId);
      await fetchHandovers();
    } catch (err) {
      console.error('Error deleting handover:', err);
      alert('Failed to delete handover');
    } finally {
      setDeleting(null);
    }
  };

  const handleFilterChange = (key, value) => {
    setFilters(prev => ({
      ...prev,
      [key]: value
    }));
    setCurrentPage(1);
  };

  const clearFilters = () => {
    setFilters({
      employee: '',
      search: ''
    });
    setCurrentPage(1);
  };

  const handleTabChange = (tabId) => {
    setActiveTab(tabId);
    setCurrentPage(1);
  };

  const getStatusBadge = (status) => {
    const statusConfig = {
      pending: { 
        color: 'bg-yellow-100 text-yellow-800 border border-yellow-200', 
        text: 'Pending',
        icon: FaClock
      },
      completed: { 
        color: 'bg-green-100 text-green-800 border border-green-200', 
        text: 'Completed',
        icon: FaCheckCircle
      },
      disputed: { 
        color: 'bg-red-100 text-red-800 border border-red-200', 
        text: 'Disputed',
        icon: FaExclamationTriangle
      }
    };
    
    const config = statusConfig[status] || { 
      color: 'bg-gray-100 text-gray-800 border border-gray-200', 
      text: status,
      icon: FaClock
    };
    
    const IconComponent = config.icon;
    
    return (
      <span className={`inline-flex items-center px-3 py-1 rounded-full text-xs font-medium ${config.color}`}>
        <IconComponent className="mr-1 h-3 w-3" />
        {config.text}
      </span>
    );
  };

  const getHandoverTypeDisplay = (type) => {
    return type === 'pickup' ? {
      text: 'Vehicle Pickup',
      icon: FaCarSide,
      color: 'text-green-600',
      bgColor: 'bg-green-50'
    } : {
      text: 'Vehicle Return',
      icon: FaClipboardCheck,
      color: 'text-blue-600',
      bgColor: 'bg-blue-50'
    };
  };

  const handleHandoverSuccess = () => {
    setShowCreateModal(false);
    fetchHandovers();
  };

  const getActiveTabInfo = () => {
    return tabs.find(tab => tab.id === activeTab);
  };

  if (loading && handovers.length === 0) {
    return <LoadingSpinner />;
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex justify-between items-center">
        <div>
          <h2 className="text-3xl font-bold text-gray-900">Vehicle Handovers</h2>
          <p className="text-gray-600 mt-1">Manage vehicle pickup and return handovers</p>
        </div>
        <button
          onClick={handleCreateHandover}
          className="bg-gradient-to-r from-blue-600 to-blue-700 hover:from-blue-700 hover:to-blue-800 text-white px-6 py-3 rounded-xl flex items-center space-x-2 transition-all duration-200 shadow-lg hover:shadow-xl transform hover:-translate-y-0.5"
        >
          <FaPlus className="h-4 w-4" />
          <span className="font-medium">Create Handover</span>
        </button>
      </div>

      {/* Tab Navigation */}
      <div className="bg-white rounded-xl shadow-sm border border-gray-200 overflow-hidden">
        <div className="flex border-b border-gray-200">
          {tabs.map((tab) => {
            const IconComponent = tab.icon;
            const isActive = activeTab === tab.id;
            
            return (
              <button
                key={tab.id}
                onClick={() => handleTabChange(tab.id)}
                className={`flex-1 px-6 py-4 text-sm font-medium transition-all duration-200 relative ${
                  isActive
                    ? `${tab.color} bg-white border-b-2 border-current`
                    : 'text-gray-500 hover:text-gray-700 hover:bg-gray-50'
                }`}
              >
                <div className="flex items-center justify-center space-x-2">
                  <IconComponent className={`h-4 w-4 ${isActive ? tab.color : ''}`} />
                  <span>{tab.name}</span>
                </div>
                {isActive && (
                  <motion.div
                    layoutId="activeTab"
                    className="absolute bottom-0 left-0 right-0 h-0.5 bg-current"
                  />
                )}
              </button>
            );
          })}
        </div>

        {/* Tab Content Header with Filters */}
        <div className="p-6 border-b border-gray-200">
          <div className="flex justify-between items-start mb-4">
            <div>
              <h3 className="text-lg font-semibold text-gray-900 flex items-center space-x-2">
                {React.createElement(getActiveTabInfo().icon, { 
                  className: `h-5 w-5 ${getActiveTabInfo().color}` 
                })}
                <span>{getActiveTabInfo().name}</span>
              </h3>
              <p className="text-gray-600 text-sm mt-1">
                {activeTab === 'pickup' && 'Track vehicle pickup handovers and documentation'}
                {activeTab === 'return' && 'Monitor vehicle return handovers and final inspection'}
                {activeTab === 'today' && `All handovers scheduled for ${moment().format('MMMM DD, YYYY')}`}
              </p>
            </div>
            <button
              onClick={() => setShowFilters(!showFilters)}
              className="text-blue-600 hover:text-blue-800 flex items-center space-x-1 px-3 py-2 rounded-lg hover:bg-blue-50 transition-colors"
            >
              <FaFilter className="h-4 w-4" />
              <span>Filters</span>
              {showFilters ? <FaChevronUp className="h-3 w-3" /> : <FaChevronDown className="h-3 w-3" />}
            </button>
          </div>

          <AnimatePresence>
            {showFilters && (
              <motion.div
                initial={{ height: 0, opacity: 0 }}
                animate={{ height: 'auto', opacity: 1 }}
                exit={{ height: 0, opacity: 0 }}
                className="grid grid-cols-1 md:grid-cols-3 gap-4"
              >
                {/* Search */}
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">Search</label>
                  <div className="relative">
                    <FaSearch className="absolute left-3 top-3 text-gray-400 h-4 w-4" />
                    <input
                      type="text"
                      placeholder="Search by customer, vehicle..."
                      value={filters.search}
                      onChange={(e) => handleFilterChange('search', e.target.value)}
                      className="pl-10 w-full border border-gray-300 rounded-lg px-3 py-2 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                    />
                  </div>
                </div>

                {/* Employee */}
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">Employee</label>
                  <select
                    value={filters.employee}
                    onChange={(e) => handleFilterChange('employee', e.target.value)}
                    className="w-full border border-gray-300 rounded-lg px-3 py-2 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                  >
                    <option value="">All Employees</option>
                    {employees.map(employee => (
                      <option key={employee._id} value={employee._id}>
                        {employee.firstName} {employee.lastName}
                      </option>
                    ))}
                  </select>
                </div>

                {/* Clear Filters */}
                <div className="flex items-end">
                  <button
                    onClick={clearFilters}
                    className="w-full bg-gray-500 hover:bg-gray-600 text-white px-4 py-2 rounded-lg transition-colors"
                  >
                    Clear Filters
                  </button>
                </div>
              </motion.div>
            )}
          </AnimatePresence>
        </div>
      </div>

      {/* Handovers Content */}
      <div className="bg-white rounded-xl shadow-sm border border-gray-200 overflow-hidden">
        {error && (
          <div className="p-4 bg-red-50 border-l-4 border-red-400 text-red-700">
            <div className="flex items-center">
              <FaExclamationTriangle className="mr-2" />
              {error}
            </div>
          </div>
        )}

        {loading ? (
          <div className="p-8 text-center">
            <LoadingSpinner />
          </div>
        ) : handovers.length === 0 ? (
          <div className="p-12 text-center text-gray-500">
            {React.createElement(getActiveTabInfo().icon, { 
              className: `mx-auto h-16 w-16 text-gray-300 mb-4` 
            })}
            <h3 className="text-xl font-medium text-gray-900 mb-2">No handovers found</h3>
            <p className="text-gray-600 mb-6">
              {activeTab === 'pickup' && 'No pickup handovers match your criteria.'}
              {activeTab === 'return' && 'No return handovers match your criteria.'}
              {activeTab === 'today' && 'No handovers scheduled for today.'}
            </p>
            <button
              onClick={handleCreateHandover}
              className="bg-blue-600 hover:bg-blue-700 text-white px-6 py-3 rounded-lg transition-colors"
            >
              Create First Handover
            </button>
          </div>
        ) : (
          <div className="divide-y divide-gray-100">
            {handovers.map((handover) => {
              const typeDisplay = getHandoverTypeDisplay(handover.handoverType);
              const TypeIcon = typeDisplay.icon;
              
              return (
                <motion.div
                  key={handover._id}
                  initial={{ opacity: 0, y: 20 }}
                  animate={{ opacity: 1, y: 0 }}
                  className="p-6 hover:bg-gray-50 transition-all duration-200"
                >
                  <div className="flex items-start justify-between">
                    <div className="flex-1">
                      {/* Header */}
                      <div className="flex items-center space-x-3 mb-3">
                        <div className={`p-2 rounded-lg ${typeDisplay.bgColor}`}>
                          <TypeIcon className={`h-5 w-5 ${typeDisplay.color}`} />
                        </div>
                        <div className="flex-1">
                          <div className="flex items-center space-x-3 mb-1">
                            <h3 className="text-lg font-semibold text-gray-900">
                              {typeDisplay.text}
                            </h3>
                            {getStatusBadge(handover.status)}
                          </div>
                          <div className="flex items-center space-x-4 text-sm text-gray-600">
                            <span className="flex items-center">
                              <FaCalendarAlt className="mr-1 h-3 w-3" />
                              {moment(handover.handoverDate).format('MMM DD, YYYY • HH:mm')}
                            </span>
                            {handover.employee && (
                              <span className="flex items-center">
                                <FaUser className="mr-1 h-3 w-3" />
                                {handover.employee.firstName} {handover.employee.lastName}
                              </span>
                            )}
                          </div>
                        </div>
                      </div>

                      {/* Vehicle and Customer Info */}
                      <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-4">
                        {handover.vehicle && (
                          <div className="bg-gray-50 rounded-lg p-3">
                            <div className="flex items-center text-sm font-medium text-gray-700 mb-1">
                              <FaCar className="mr-2 h-4 w-4" />
                              Vehicle
                            </div>
                            <p className="text-gray-900 font-medium">
                              {handover.vehicle.make} {handover.vehicle.model} {handover.vehicle.year}
                            </p>
                            {handover.vehicle.licensePlate && (
                              <p className="text-gray-600 text-sm">{handover.vehicle.licensePlate}</p>
                            )}
                          </div>
                        )}
                        
                        <div className="bg-gray-50 rounded-lg p-3">
                          <div className="flex items-center text-sm font-medium text-gray-700 mb-1">
                            <FaUser className="mr-2 h-4 w-4" />
                            Customer
                          </div>
                          <p className="text-gray-900 font-medium">{handover.customer?.name}</p>
                          <p className="text-gray-600 text-sm">{handover.customer?.email}</p>
                        </div>
                      </div>

                      {/* Quick Stats */}
                      <div className="flex items-center space-x-6 mb-4">
                        <div className="flex items-center text-sm">
                          <FaTachometerAlt className="mr-2 h-4 w-4 text-gray-400" />
                          <span className="font-medium text-gray-700">Mileage:</span>
                          <span className="ml-1 text-gray-900">{handover.mileage?.toLocaleString()} km</span>
                        </div>
                        <div className="flex items-center text-sm">
                          <FaGasPump className="mr-2 h-4 w-4 text-gray-400" />
                          <span className="font-medium text-gray-700">Fuel:</span>
                          <span className="ml-1 text-gray-900 capitalize">{handover.fuelLevel}</span>
                        </div>
                        <div className="flex items-center text-sm">
                          <span className="font-medium text-gray-700">Condition:</span>
                          <span className="ml-1 text-gray-900 capitalize">
                            {handover.condition?.exterior} / {handover.condition?.interior}
                          </span>
                        </div>
                      </div>

                      {/* Media and Damages Indicators */}
                      <div className="flex items-center space-x-4">
                        {/* Media indicators */}
                        <div className="flex items-center space-x-2">
                          {handover.photos?.length > 0 && (
                            <span className="inline-flex items-center text-xs bg-blue-100 text-blue-700 px-2 py-1 rounded-full font-medium">
                              <FaImage className="mr-1 h-3 w-3" />
                              {handover.photos.length} photo{handover.photos.length !== 1 ? 's' : ''}
                            </span>
                          )}
                          {handover.videos?.length > 0 && (
                            <span className="inline-flex items-center text-xs bg-purple-100 text-purple-700 px-2 py-1 rounded-full font-medium">
                              <FaVideo className="mr-1 h-3 w-3" />
                              {handover.videos.length} video{handover.videos.length !== 1 ? 's' : ''}
                            </span>
                          )}
                          {handover.voiceNotes?.length > 0 && (
                            <span className="inline-flex items-center text-xs bg-green-100 text-green-700 px-2 py-1 rounded-full font-medium">
                              <FaMicrophone className="mr-1 h-3 w-3" />
                              {handover.voiceNotes.length} note{handover.voiceNotes.length !== 1 ? 's' : ''}
                            </span>
                          )}
                        </div>

                        {/* Damages indicator */}
                        {handover.damages?.length > 0 && (
                          <span className="inline-flex items-center text-xs bg-red-100 text-red-700 px-2 py-1 rounded-full font-medium">
                            <FaExclamationTriangle className="mr-1 h-3 w-3" />
                            {handover.damages.length} damage{handover.damages.length !== 1 ? 's' : ''}
                          </span>
                        )}
                      </div>
                    </div>

                    {/* Actions */}
                    <div className="flex items-center space-x-2 ml-4">
                      <button
                        onClick={() => handleViewHandover(handover)}
                        className="text-blue-600 hover:text-blue-800 p-2 rounded-lg hover:bg-blue-50 transition-colors"
                        title="View Details"
                      >
                        <FaEye className="h-4 w-4" />
                      </button>
                      <button
                        onClick={() => handleDeleteHandover(handover._id)}
                        disabled={deleting === handover._id}
                        className="text-red-600 hover:text-red-800 p-2 rounded-lg hover:bg-red-50 transition-colors disabled:opacity-50"
                        title="Delete"
                      >
                        {deleting === handover._id ? (
                          <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-red-600"></div>
                        ) : (
                          <FaTrash className="h-4 w-4" />
                        )}
                      </button>
                    </div>
                  </div>
                </motion.div>
              );
            })}
          </div>
        )}

        {/* Pagination */}
        {totalPages > 1 && (
          <div className="p-6 border-t bg-gray-50 flex justify-between items-center">
            <div className="text-sm text-gray-600">
              Page {currentPage} of {totalPages}
            </div>
            <div className="flex space-x-2">
              <button
                onClick={() => setCurrentPage(prev => Math.max(1, prev - 1))}
                disabled={currentPage === 1}
                className="px-4 py-2 border border-gray-300 rounded-lg disabled:opacity-50 disabled:cursor-not-allowed hover:bg-gray-100 transition-colors font-medium"
              >
                Previous
              </button>
              <button
                onClick={() => setCurrentPage(prev => Math.min(totalPages, prev + 1))}
                disabled={currentPage === totalPages}
                className="px-4 py-2 border border-gray-300 rounded-lg disabled:opacity-50 disabled:cursor-not-allowed hover:bg-gray-100 transition-colors font-medium"
              >
                Next
              </button>
            </div>
          </div>
        )}
      </div>

      {/* Create Handover Modal */}
      <AnimatePresence>
        {showCreateModal && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4"
          >
            <motion.div
              initial={{ scale: 0.95, opacity: 0 }}
              animate={{ scale: 1, opacity: 1 }}
              exit={{ scale: 0.95, opacity: 0 }}
              className="bg-white rounded-xl max-w-4xl w-full max-h-[90vh] overflow-hidden shadow-2xl"
            >
              <div className="p-6 border-b border-gray-200 flex justify-between items-center">
                <h3 className="text-xl font-semibold text-gray-900">Create New Handover</h3>
                <button
                  onClick={() => setShowCreateModal(false)}
                  className="text-gray-400 hover:text-gray-600 text-2xl"
                >
                  ×
                </button>
              </div>
              <div className="overflow-y-auto max-h-[calc(90vh-100px)]">
                <HandoverForm
                  isModal={true}
                  onClose={() => setShowCreateModal(false)}
                  onSubmitSuccess={handleHandoverSuccess}
                />
              </div>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>

      {/* Handover Details Modal */}
      <AnimatePresence>
        {showDetailsModal && selectedHandover && (
          <HandoverDetailsModal
            handoverId={selectedHandover._id}
            onClose={() => {
              setShowDetailsModal(false);
              setSelectedHandover(null);
            }}
          />
        )}
      </AnimatePresence>
    </div>
  );
};

export default Handovers;
